import { useCallback, useEffect, useState } from "react";
import { friendsNotifications } from "@/lib/api";

/**
 * useFriendNotifications — polls /friends/notifications every 30s to drive
 * the HUD badge (red dot + count on the user-menu trigger + the FRIENDS
 * dropdown entry).
 *
 * Returns `{ incomingRequests, unreadMessages, total, refresh }`.
 *
 * Polling is gated on `enabled` so we don't fire requests for logged-out
 * users or while another modal is consuming bandwidth on its own poller.
 */
const POLL_MS = 30_000;

export default function useFriendNotifications({ enabled = true } = {}) {
  const [data, setData] = useState({ incoming_requests: 0, unread_messages: 0, total: 0 });

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await friendsNotifications();
      setData(r);
    } catch (_e) {
      // Silent — transient failures shouldn't toast-spam during gameplay.
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [enabled, refresh]);

  return {
    incomingRequests: data.incoming_requests || 0,
    unreadMessages: data.unread_messages || 0,
    total: data.total || 0,
    refresh,
  };
}
